/**
 * mixed-engine の wasm-bindgen ラッパ。
 *
 * `wasm-pack build crates/mixed-engine --target web` で `pkg/` を生成、
 * このモジュールがそれを lazy init して、ゲーム別の strongly-typed なエントリポイントを返す。
 *
 * State / Move の中身はサーバ側の serde 形と完全に同じ JSON 形 (snake_case 等)。
 * 楽観更新でこの apply を通したのち、サーバの move_applied で確定する。
 */

import init, * as engine from './pkg/mixed_engine';

let initialized: Promise<void> | null = null;

export async function ensureWasm(): Promise<void> {
  if (!initialized) {
    // 失敗した Promise をキャッシュに焼き付けると以後リトライ不能になる
    // (mobile 回線での .wasm 取得失敗が回復不能に)。 失敗時は null に戻して
    // 次の呼び出し (= UI の再読み込み) でやり直せるようにする。
    initialized = init()
      .then(() => undefined)
      .catch((e) => {
        initialized = null;
        throw e;
      });
  }
  return initialized;
}

export type GameId =
  | 'Reversi'
  | 'AnimalShogi'
  | 'Backgammon'
  | 'Renju'
  | 'Chess'
  | 'Shogi'
  | 'Jungo'
  | 'Connect6'
  | 'Kalah'
  | 'TwixT'
  | 'Xiangqi'
  | 'Janggi'
  | 'Draughts'
  | 'HeadsUpPoker'
  | 'Abalone'
  | 'ChuShogi'
  | 'Kyoen'
  | 'Gomoku'
  | 'BridgeNT'
  | 'Trax'
  | 'Amazons';

// 型は any 同等で扱う (構造はサーバの serde と一致)。
// 必要に応じて zod / ts-pattern で精緻化する。
export type GameState = unknown;
export type GameMove = unknown;
export type GameOutcome = unknown;

export interface EngineApi {
  init(): GameState;
  legal(state: GameState): GameMove[];
  apply(state: GameState, mv: GameMove): GameState;
  terminal(state: GameState): GameOutcome | null;
}

const reversiApi: EngineApi = {
  init: () => engine.reversiInit(),
  legal: (s) => engine.reversiLegal(s) as GameMove[],
  apply: (s, m) => engine.reversiApply(s, m),
  terminal: (s) => (engine.reversiTerminal(s) as GameOutcome | null) ?? null,
};

const animalApi: EngineApi = {
  init: () => engine.animalInit(),
  legal: (s) => engine.animalLegal(s) as GameMove[],
  apply: (s, m) => engine.animalApply(s, m),
  terminal: (s) => (engine.animalTerminal(s) as GameOutcome | null) ?? null,
};

const backgammonApi: EngineApi = {
  init: () => engine.backgammonInit(),
  legal: (s) => engine.backgammonLegal(s) as GameMove[],
  apply: (s, m) => engine.backgammonApply(s, m),
  terminal: (s) => (engine.backgammonTerminal(s) as GameOutcome | null) ?? null,
};

const renjuApi: EngineApi = {
  init: () => engine.renjuInit(),
  legal: (s) => engine.renjuLegal(s) as GameMove[],
  apply: (s, m) => engine.renjuApply(s, m),
  terminal: (s) => (engine.renjuTerminal(s) as GameOutcome | null) ?? null,
};

/** Renju 限定: 黒の番のとき着手禁止セル (= 黒禁手) を返す。 */
export function renjuForbiddenCells(state: GameState): [number, number][] {
  return (engine.renjuForbiddenCells(state) as [number, number][]) ?? [];
}

/**
 * Renju 限定: 開局規定で今おける点。 制限が無い段階では空配列。
 *
 * 4 手目の範囲は正方形ではなく珠形ごとの表で決まるので、 盤に出さないと
 * 打ち手が推測できない。
 */
export function renjuOpeningCells(state: GameState): [number, number][] {
  return (engine.renjuOpeningCells(state) as [number, number][]) ?? [];
}

/** Renju 限定: 盤上の 3 石が決めた珠形の `[短縮名, 日本語名]`。 未確定なら null。 */
export function renjuOpeningName(state: GameState): [string, string] | null {
  const v = engine.renjuOpeningName(state) as string[] | undefined;
  return v && v.length === 2 ? [v[0], v[1]] : null;
}

const chessApi: EngineApi = {
  init: () => engine.chessInit(),
  legal: (s) => engine.chessLegal(s) as GameMove[],
  apply: (s, m) => engine.chessApply(s, m),
  terminal: (s) => (engine.chessTerminal(s) as GameOutcome | null) ?? null,
};

const shogiApi: EngineApi = {
  init: () => engine.shogiInit(),
  legal: (s) => engine.shogiLegal(s) as GameMove[],
  apply: (s, m) => engine.shogiApply(s, m),
  terminal: (s) => (engine.shogiTerminal(s) as GameOutcome | null) ?? null,
};

const jungoApi: EngineApi = {
  init: () => engine.jungoInit(),
  legal: (s) => engine.jungoLegal(s) as GameMove[],
  apply: (s, m) => engine.jungoApply(s, m),
  terminal: (s) => (engine.jungoTerminal(s) as GameOutcome | null) ?? null,
};

const connect6Api: EngineApi = {
  init: () => engine.connect6Init(),
  legal: (s) => engine.connect6Legal(s) as GameMove[],
  apply: (s, m) => engine.connect6Apply(s, m),
  terminal: (s) => (engine.connect6Terminal(s) as GameOutcome | null) ?? null,
};

const kalahApi: EngineApi = {
  init: () => engine.kalahInit(),
  legal: (s) => engine.kalahLegal(s) as GameMove[],
  apply: (s, m) => engine.kalahApply(s, m),
  terminal: (s) => (engine.kalahTerminal(s) as GameOutcome | null) ?? null,
};

const twixtApi: EngineApi = {
  init: () => engine.twixtInit(),
  legal: (s) => engine.twixtLegal(s) as GameMove[],
  apply: (s, m) => engine.twixtApply(s, m),
  terminal: (s) => (engine.twixtTerminal(s) as GameOutcome | null) ?? null,
};

const xiangqiApi: EngineApi = {
  init: () => engine.xiangqiInit(),
  legal: (s) => engine.xiangqiLegal(s) as GameMove[],
  apply: (s, m) => engine.xiangqiApply(s, m),
  terminal: (s) => (engine.xiangqiTerminal(s) as GameOutcome | null) ?? null,
};

const janggiApi: EngineApi = {
  init: () => engine.janggiInit(),
  legal: (s) => engine.janggiLegal(s) as GameMove[],
  apply: (s, m) => engine.janggiApply(s, m),
  terminal: (s) => (engine.janggiTerminal(s) as GameOutcome | null) ?? null,
};

const draughtsApi: EngineApi = {
  init: () => engine.draughtsInit(),
  legal: (s) => engine.draughtsLegal(s) as GameMove[],
  apply: (s, m) => engine.draughtsApply(s, m),
  terminal: (s) => (engine.draughtsTerminal(s) as GameOutcome | null) ?? null,
};

const headsUpPokerApi: EngineApi = {
  init: () => engine.headsUpPokerInit(),
  legal: (s) => engine.headsUpPokerLegal(s) as GameMove[],
  apply: (s, m) => engine.headsUpPokerApply(s, m),
  terminal: (s) => (engine.headsUpPokerTerminal(s) as GameOutcome | null) ?? null,
};

// 旧 jungoPassAlive は廃止。 pass-alive は JungoState.pass_alive にキャッシュ済み。

const abaloneApi: EngineApi = {
  init: () => engine.abaloneInit(),
  legal: (s) => engine.abaloneLegal(s) as GameMove[],
  apply: (s, m) => engine.abaloneApply(s, m),
  terminal: (s) => (engine.abaloneTerminal(s) as GameOutcome | null) ?? null,
};

const chuShogiApi: EngineApi = {
  init: () => engine.chuShogiInit(),
  legal: (s) => engine.chuShogiLegal(s) as GameMove[],
  apply: (s, m) => engine.chuShogiApply(s, m),
  terminal: (s) => (engine.chuShogiTerminal(s) as GameOutcome | null) ?? null,
};

const kyoenApi: EngineApi = {
  init: () => engine.kyoenInit(),
  legal: (s) => engine.kyoenLegal(s) as GameMove[],
  apply: (s, m) => engine.kyoenApply(s, m),
  terminal: (s) => (engine.kyoenTerminal(s) as GameOutcome | null) ?? null,
};

const gomokuApi: EngineApi = {
  init: () => engine.gomokuInit(),
  legal: (s) => engine.gomokuLegal(s) as GameMove[],
  apply: (s, m) => engine.gomokuApply(s, m),
  terminal: (s) => (engine.gomokuTerminal(s) as GameOutcome | null) ?? null,
};

const bridgeNtApi: EngineApi = {
  init: () => engine.bridgeNtInit(),
  legal: (s) => engine.bridgeNtLegal(s) as GameMove[],
  apply: (s, m) => engine.bridgeNtApply(s, m),
  terminal: (s) => (engine.bridgeNtTerminal(s) as GameOutcome | null) ?? null,
};

const traxApi: EngineApi = {
  init: () => engine.traxInit(),
  legal: (s) => engine.traxLegal(s) as GameMove[],
  apply: (s, m) => engine.traxApply(s, m),
  terminal: (s) => (engine.traxTerminal(s) as GameOutcome | null) ?? null,
};

const amazonsApi: EngineApi = {
  init: () => engine.amazonsInit(),
  legal: (s) => engine.amazonsLegal(s) as GameMove[],
  apply: (s, m) => engine.amazonsApply(s, m),
  terminal: (s) => (engine.amazonsTerminal(s) as GameOutcome | null) ?? null,
};

export function getEngine(game: GameId): EngineApi {
  switch (game) {
    case 'Reversi':
      return reversiApi;
    case 'AnimalShogi':
      return animalApi;
    case 'Backgammon':
      return backgammonApi;
    case 'Renju':
      return renjuApi;
    case 'Chess':
      return chessApi;
    case 'Shogi':
      return shogiApi;
    case 'Jungo':
      return jungoApi;
    case 'Connect6':
      return connect6Api;
    case 'Kalah':
      return kalahApi;
    case 'TwixT':
      return twixtApi;
    case 'Xiangqi':
      return xiangqiApi;
    case 'Janggi':
      return janggiApi;
    case 'Draughts':
      return draughtsApi;
    case 'HeadsUpPoker':
      return headsUpPokerApi;
    case 'Abalone':
      return abaloneApi;
    case 'ChuShogi':
      return chuShogiApi;
    case 'Kyoen':
      return kyoenApi;
    case 'Gomoku':
      return gomokuApi;
    case 'BridgeNT':
      return bridgeNtApi;
    case 'Trax':
      return traxApi;
    case 'Amazons':
      return amazonsApi;
  }
}

// ───────── VRF ─────────

export interface VrfKeyPair {
  secret: Uint8Array;
  public: Uint8Array;
}

export function vrfGenerateKey(): VrfKeyPair {
  const r = engine.vrfGenerateKey() as { secret: number[]; public: number[] };
  return {
    secret: Uint8Array.from(r.secret),
    public: Uint8Array.from(r.public),
  };
}

export function vrfEvaluate(secret: Uint8Array, input: Uint8Array): Uint8Array {
  const proof = engine.vrfEvaluate(secret, input) as number[];
  return Uint8Array.from(proof);
}

export function vrfVerify(
  pk: Uint8Array,
  input: Uint8Array,
  proof: Uint8Array,
): boolean {
  return engine.vrfVerify(pk, input, proof);
}

export function vrfCombineSeed(
  hostProof: Uint8Array,
  guestProof: Uint8Array,
  ctx: Uint8Array,
): Uint8Array {
  const seed = engine.vrfCombineSeed(hostProof, guestProof, ctx) as number[];
  return Uint8Array.from(seed);
}

export function vrfDeriveDice(seed: Uint8Array, turn: number, sub: number): number {
  return engine.vrfDeriveDice(seed, turn, sub);
}

// ───────── Match runtime (ローカル CPU 対戦用) ─────────

import type { BoardId, MatchConfig } from '../proto';

export type MatchState = unknown; // 内部 shape は MatchState (Rust) と一致
export type MatchOutcome = 'First' | 'Second' | 'Draw';

export interface MatchApplyResult {
  state: MatchState;
  history_entry: unknown;
  board_result: MatchOutcome | null;
  match_outcome: MatchOutcome | null;
  clock_snapshot: unknown;
}

export function matchOpen(config: MatchConfig, nowMs: number): MatchState {
  // wasm 側は JSON 文字列を返す (ES Map 化を回避するため)
  const s = engine.matchOpen(config, nowMs) as string;
  return JSON.parse(s) as MatchState;
}

/** `matchOpen` + 公平 seat ランダム化 + initial turn mask 再計算を一括実行。
 *  ローカル CPU 対戦専用 (TS 側で再実装してた randomization をすべて Rust に閉じ込める)。
 *  `hostPref` は `'random' | 'host' | 'guest'`。 host_seat の決定方法を指定する。 */
export function matchOpenWithSeats(
  config: MatchConfig,
  nowMs: number,
  hostPref: 'random' | 'host' | 'guest',
): MatchState {
  const s = engine.matchOpenWithSeats(config, nowMs, hostPref) as string;
  return JSON.parse(s) as MatchState;
}

export function matchApplyMove(
  state: MatchState,
  config: MatchConfig,
  board: BoardId,
  seat: 'Leader' | 'Follower',
  mv: unknown,
  nowMs: number,
): MatchApplyResult {
  // state は JSON 文字列で渡す。 wasm 側は as_string() で受ける。
  // seat は MatchSeat (Leader/Follower)。 wasm 側で各 board の first_seat を見て Side に変換。
  const stateStr = JSON.stringify(state);
  const r = engine.matchApplyMove(stateStr, config, board, seat, mv, nowMs) as string;
  return JSON.parse(r) as MatchApplyResult;
}

export function matchApplyBoardAction(
  state: MatchState,
  config: MatchConfig,
  board: BoardId,
  seat: 'Leader' | 'Follower',
  action: 'Resign' | 'OfferDraw' | 'AcceptDraw' | 'DeclineDraw',
): MatchApplyResult {
  const stateStr = JSON.stringify(state);
  const r = engine.matchApplyBoardAction(stateStr, config, board, seat, action) as string;
  return JSON.parse(r) as MatchApplyResult;
}

export function matchClockSnapshot(
  state: MatchState,
  config: MatchConfig,
  nowMs: number,
): unknown {
  const stateStr = JSON.stringify(state);
  const r = engine.matchClockSnapshot(stateStr, config, nowMs) as string;
  return JSON.parse(r);
}
